import axios from 'axios';

// Safe API base resolution:
// 1. If REACT_APP_BACKEND_URL is set (and truthy), use it
// 2. Otherwise, use same-origin /api (works for standard Emergent deployments)
// Guards against "undefined" literal, empty string, or missing env var.
const raw = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = (raw && raw !== 'undefined' && raw !== 'null') ? raw.replace(/\/+$/, '') : '';
export const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// SEC-06 / D-2: the session JWT now lives in an httpOnly cookie set by the
// backend, NOT in localStorage (which any XSS could read). withCredentials:true
// makes the browser attach that cookie (and the readable tfd_csrf cookie) to
// every request. We no longer attach an Authorization header from JS.
const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// CSRF cookie name (readable, double-submit). Mirrors backend csrf.CSRF_COOKIE_NAME.
const CSRF_COOKIE_NAME = 'tfd_csrf';

function readCookie(name) {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const prefix = `${name}=`;
  const parts = document.cookie.split(';');
  for (let part of parts) {
    part = part.trim();
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
}

// SEC-06 / D-2: on mutating requests, echo the double-submit CSRF token from the
// readable tfd_csrf cookie back in the X-CSRF-Token header. The backend rejects
// cookie-authenticated mutations whose header doesn't match the cookie, which a
// cross-site attacker can't forge (it can't read the cookie).
const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (MUTATING_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE_NAME);
    if (csrf) {
      config.headers['X-CSRF-Token'] = csrf;
    }
  }
  // FormData bodies: drop the default JSON Content-Type so the browser sets
  // multipart/form-data with the correct boundary.
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

// Handle 401 responses. The session is a cookie now, so there is no JS token to
// clear — the AuthContext drops the in-memory user and the backend cookie either
// expired or was cleared via /auth/logout.
api.interceptors.response.use(
  (res) => res,
  (err) => Promise.reject(err)
);

export default api;
